import type { Scope } from "@/domain/social/SocialRepository";

export interface LocalTrackContinentAsset {
	readonly id: string;
	readonly mediaType: "image" | "video";
	readonly filename: string;
	readonly blob: Blob;
}

export async function listLocalTrackAssets(
	scope: Scope,
): Promise<readonly LocalTrackAsset[]> {
	const database = await openDatabase();
	return new Promise<readonly LocalTrackAsset[]>((resolve, reject) => {
		const transaction = database.transaction(TRACK_STORE, "readonly");
		const request = transaction.objectStore(TRACK_STORE).getAll();
		request.onsuccess = () => {
			const values = (request.result as StoredTrackAsset[])
				.filter(
					(value) =>
						value.ownerUserId === scope.ownerUserId &&
						value.scopeEpoch === scope.scopeEpoch,
				)
				.sort((left, right) => right.updatedAt - left.updatedAt)
				.map(stripKey);
			resolve(values);
		};
		request.onerror = () =>
			reject(request.error ?? new Error("track-asset-list-failed"));
		transaction.oncomplete = () => database.close();
		transaction.onerror = () => database.close();
	});
}

export interface LocalTrackAsset {
	readonly id: string;
	readonly ownerUserId: string;
	readonly scopeEpoch: number;
	readonly title: string;
	readonly audioFilename: string;
	readonly audioMimeType: string;
	readonly audioBlob: Blob;
	readonly durationSeconds: number;
	readonly artworkFilename: string | null;
	readonly artworkBlob: Blob | null;
	readonly lyricsFilename: string | null;
	readonly lyricsText: string;
	readonly continents: readonly LocalTrackContinentAsset[];
	readonly createdAt: number;
	readonly updatedAt: number;
}

export interface CreateLocalTrackAssetInput {
	readonly id: string;
	readonly title: string;
	readonly audioFile: File;
	readonly durationSeconds: number;
	readonly artworkFile: File | null;
	readonly lyricsFilename: string | null;
	readonly lyricsText: string;
}

export interface UpdateLocalTrackAssetInput {
	readonly title: string;
	readonly artworkFile?: File | null;
	readonly lyricsFilename: string | null;
	readonly lyricsText: string;
	readonly continents: readonly LocalTrackContinentAsset[];
}

const DATABASE_NAME = "artist-local-media-v1";
const DATABASE_VERSION = 1;
const TRACK_STORE = "tracks";

interface StoredTrackAsset extends LocalTrackAsset {
	readonly key: string;
}

function keyFor(scope: Scope, id: string): string {
	return `${scope.ownerUserId}:${scope.scopeEpoch}:${id}`;
}

export async function createLocalTrackAsset(
	scope: Scope,
	input: CreateLocalTrackAssetInput,
): Promise<LocalTrackAsset> {
	const now = Date.now();
	const asset: StoredTrackAsset = {
		key: keyFor(scope, input.id),
		id: input.id,
		ownerUserId: scope.ownerUserId,
		scopeEpoch: scope.scopeEpoch,
		title: input.title.trim(),
		audioFilename: input.audioFile.name,
		audioMimeType: input.audioFile.type || "application/octet-stream",
		audioBlob: input.audioFile,
		durationSeconds: input.durationSeconds,
		artworkFilename: input.artworkFile?.name ?? null,
		artworkBlob: input.artworkFile,
		lyricsFilename: input.lyricsFilename,
		lyricsText: input.lyricsText,
		continents: [],
		createdAt: now,
		updatedAt: now,
	};
	await putStored(asset);
	return stripKey(asset);
}

export async function loadLocalTrackAsset(
	scope: Scope,
	id: string,
): Promise<LocalTrackAsset | null> {
	const database = await openDatabase();
	return new Promise<LocalTrackAsset | null>((resolve, reject) => {
		const transaction = database.transaction(TRACK_STORE, "readonly");
		const request = transaction.objectStore(TRACK_STORE).get(keyFor(scope, id));
		request.onsuccess = () => {
			const value = request.result as StoredTrackAsset | undefined;
			resolve(value === undefined ? null : stripKey(value));
		};
		request.onerror = () => reject(request.error ?? new Error("track-asset-read-failed"));
		transaction.oncomplete = () => database.close();
		transaction.onerror = () => database.close();
	});
}

export async function updateLocalTrackAsset(
	scope: Scope,
	id: string,
	input: UpdateLocalTrackAssetInput,
): Promise<LocalTrackAsset> {
	const current = await loadStored(scope, id);
	if (current === null) throw new Error("track-asset-not-found");
	const next: StoredTrackAsset = {
		...current,
		title: input.title.trim(),
		artworkFilename:
			input.artworkFile === undefined
				? current.artworkFilename
				: input.artworkFile?.name ?? null,
		artworkBlob:
			input.artworkFile === undefined
				? current.artworkBlob
				: input.artworkFile,
		lyricsFilename: input.lyricsFilename,
		lyricsText: input.lyricsText,
		continents: input.continents.map((item) => ({ ...item })),
		updatedAt: Date.now(),
	};
	await putStored(next);
	return stripKey(next);
}

export async function deleteLocalTrackAsset(scope: Scope, id: string): Promise<void> {
	const database = await openDatabase();
	await new Promise<void>((resolve, reject) => {
		const transaction = database.transaction(TRACK_STORE, "readwrite");
		transaction.objectStore(TRACK_STORE).delete(keyFor(scope, id));
		transaction.oncomplete = () => {
			database.close();
			resolve();
		};
		transaction.onerror = () => {
			const error = transaction.error ?? new Error("track-asset-delete-failed");
			database.close();
			reject(error);
		};
	});
}

export async function measureAudioDuration(file: File): Promise<number> {
	if (typeof document === "undefined") return 0;
	const url = URL.createObjectURL(file);
	try {
		return await new Promise<number>((resolve) => {
			const audio = document.createElement("audio");
			const finish = (value: number) => {
				audio.removeAttribute("src");
				audio.load();
				resolve(value);
			};
			audio.preload = "metadata";
			audio.onloadedmetadata = () =>
				finish(Number.isFinite(audio.duration) ? Math.max(0, audio.duration) : 0);
			audio.onerror = () => finish(0);
			audio.src = url;
		});
	} finally {
		URL.revokeObjectURL(url);
	}
}

async function loadStored(scope: Scope, id: string): Promise<StoredTrackAsset | null> {
	const database = await openDatabase();
	return new Promise<StoredTrackAsset | null>((resolve, reject) => {
		const transaction = database.transaction(TRACK_STORE, "readonly");
		const request = transaction.objectStore(TRACK_STORE).get(keyFor(scope, id));
		request.onsuccess = () => resolve((request.result as StoredTrackAsset | undefined) ?? null);
		request.onerror = () => reject(request.error ?? new Error("track-asset-read-failed"));
		transaction.oncomplete = () => database.close();
		transaction.onerror = () => database.close();
	});
}

async function putStored(value: StoredTrackAsset): Promise<void> {
	const database = await openDatabase();
	await new Promise<void>((resolve, reject) => {
		const transaction = database.transaction(TRACK_STORE, "readwrite");
		transaction.objectStore(TRACK_STORE).put(value);
		transaction.oncomplete = () => {
			database.close();
			resolve();
		};
		transaction.onerror = () => {
			const error = transaction.error ?? new Error("track-asset-write-failed");
			database.close();
			reject(error);
		};
	});
}

function openDatabase(): Promise<IDBDatabase> {
	if (typeof indexedDB === "undefined") {
		return Promise.reject(new Error("indexeddb-unavailable"));
	}
	return new Promise<IDBDatabase>((resolve, reject) => {
		const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
		request.onupgradeneeded = () => {
			const database = request.result;
			if (!database.objectStoreNames.contains(TRACK_STORE)) {
				database.createObjectStore(TRACK_STORE, { keyPath: "key" });
			}
		};
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error ?? new Error("indexeddb-open-failed"));
	});
}

function stripKey(value: StoredTrackAsset): LocalTrackAsset {
	const { key: _key, ...asset } = value;
	return {
		...asset,
		continents: asset.continents.map((item) => ({ ...item })),
	};
}
